import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import usePageTracker from "@/hooks/usePageTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Package, Truck, Phone, Mail, Plus, Minus, Check, FileText, Search, Loader2, LinkIcon, UserCheck } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { PhoneInput } from "@/components/PhoneInput";

import getApiUrl from "@/utils/api";

const API = getApiUrl();

export default function ShippingPage() {
  usePageTracker("Shipping");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [linkedBooking, setLinkedBooking] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState("");
  
  const [formData, setFormData] = useState({
    full_name: "",
    phone_number: "",
    email: "",
    pickup_address: "",
    dropoff_address: "",
    preferred_date: "",
    notes: "",
    booking_confirmation: ""
  });

  const [items, setItems] = useState({
    suitcases: 0,
    barrels: 0,
    boxes: 0,
    bicycles: 0,
    scooters: 0,
    tvs: 0,
    miscellaneous: 0
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const updateItemCount = (item, delta) => {
    setItems(prev => ({
      ...prev,
      [item]: Math.max(0, prev[item] + delta)
    }));
  };

  const getTotalItems = () => {
    return Object.values(items).reduce((sum, count) => sum + count, 0);
  };

  const lookupBooking = async () => {
    if (!confirmationInput || confirmationInput.length < 4) {
      toast.error("Enter at least 4 characters of your confirmation number");
      return;
    }
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API}/shipping/lookup-booking/${confirmationInput}`);
      if (res.data.found) {
        setLinkedBooking(res.data);
        setFormData(prev => ({
          ...prev,
          full_name: res.data.full_name || prev.full_name,
          phone_number: res.data.phone || prev.phone_number,
          email: res.data.email || prev.email,
          dropoff_address: res.data.destination || prev.dropoff_address,
          preferred_date: res.data.travel_date || prev.preferred_date,
          booking_confirmation: res.data.confirmation || confirmationInput
        }));
        toast.success(`Booking found! Linked to ${res.data.full_name}'s trip on ${res.data.travel_date}`);
      } else {
        toast.error("No booking found with that confirmation number");
        setLinkedBooking(null);
      }
    } catch (e) {
      toast.error("Lookup failed");
    }
    setLookupLoading(false);
  };

  const [submittedData, setSubmittedData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.phone_number || !formData.pickup_address || !formData.dropoff_address) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (getTotalItems() === 0) {
      toast.error("Please add at least one item to ship");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/shipping/request`, {
        ...formData,
        items,
        total_items: getTotalItems()
      });
      
      setSubmittedData({ ...formData, items, request_id: res.data.request_id });
      setSubmitted(true);
      toast.success("Shipping request submitted! We'll contact you shortly.");
    } catch (error) {
      toast.error("Failed to submit request. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
        <div className="max-w-lg mx-auto">
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white text-center py-8">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10" />
              </div>
              <CardTitle className="text-2xl font-bold">Request Submitted!</CardTitle>
            </CardHeader>
            <CardContent className="p-8 text-center">
              <p className="text-lg mb-4">Thank you for your shipping request!</p>
              {linkedBooking && (
                <div className="bg-blue-50 rounded-xl p-4 mb-4 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <LinkIcon className="w-4 h-4 text-blue-600" />
                    <p className="font-semibold text-blue-800 text-sm">Linked to Travel Booking</p>
                  </div>
                  <p className="text-sm text-blue-700">Confirmation: #{linkedBooking.confirmation}</p>
                  <p className="text-sm text-blue-700">Travel Date: {linkedBooking.travel_date}</p>
                  <p className="text-xs text-blue-500 mt-1">Your driver will see your shipping items and plan space accordingly.</p>
                </div>
              )}
              <p className="text-muted-foreground mb-6">
                Our team at T&M Courier Services will contact you shortly to confirm details and schedule your pickup.
              </p>
              
              <div className="bg-green-50 rounded-xl p-4 mb-6">
                <p className="font-bold text-green-800 mb-2">Contact Us:</p>
                <p className="flex items-center justify-center gap-2 text-green-700">
                  <Phone className="w-4 h-4" />
                  (815) 914-8139
                </p>
                <p className="flex items-center justify-center gap-2 text-green-700">
                  <Mail className="w-4 h-4" />
                  Tmcourierservice1@gmail.com
                </p>
              </div>

              <Link
                to={`/bill-of-lading?name=${encodeURIComponent(submittedData?.full_name || '')}&phone=${encodeURIComponent(submittedData?.phone_number || '')}&from=${encodeURIComponent(submittedData?.pickup_address || '')}`}
              >
                <Button className="w-full bg-orange-500 hover:bg-orange-600 rounded-full px-8 mb-4" data-testid="generate-bol-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Bill of Lading
                </Button>
              </Link>
              
              {!linkedBooking && (
                <Link to="/book">
                  <Button variant="outline" className="w-full rounded-full px-8 mb-4 border-green-300 text-green-700 hover:bg-green-50" data-testid="book-ride-btn">
                    <Truck className="w-4 h-4 mr-2" />
                    Book a Ride Too? Save $15/person!
                  </Button>
                </Link>
              )}
              
              <Link to="/">
                <Button variant="outline" className="rounded-full px-8">
                  Back to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-green-600 hover:text-green-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center">
              <Truck className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            T&M Courier Services
          </h1>
          <p className="text-muted-foreground">
            Ship your barrels, suitcases, and more
          </p>
        </div>

        {/* Link to Booking */}
        <Card className="border-0 shadow-md rounded-2xl mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500" data-testid="link-booking-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <LinkIcon className="w-5 h-5 text-blue-600" />
              <p className="font-bold text-blue-800 text-sm">Already booked a ride with us?</p>
            </div>
            <p className="text-xs text-blue-600 mb-3">
              Enter your booking confirmation number to auto-fill your details and link shipping to your trip. Your driver will see your items and plan space.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. 9818A05D"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value.toUpperCase())}
                className="h-10 uppercase flex-1 font-mono"
                data-testid="shipping-confirmation-input"
              />
              <Button
                type="button"
                onClick={lookupBooking}
                disabled={lookupLoading || !confirmationInput}
                className="h-10 bg-blue-600 hover:bg-blue-700"
                data-testid="shipping-lookup-btn"
              >
                {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {linkedBooking && (
              <div className="mt-3 flex items-center gap-2 bg-green-100 rounded-lg p-2.5">
                <UserCheck className="w-4 h-4 text-green-700 flex-shrink-0" />
                <div className="text-xs text-green-800">
                  <span className="font-semibold">Linked!</span> {linkedBooking.full_name} — {linkedBooking.trip_type === "return" ? "Return" : "Arrival"} on {linkedBooking.travel_date}
                  <Badge className="ml-2 text-[9px] bg-green-200 text-green-800">#{linkedBooking.confirmation}</Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Banner */}
        <Card className="border-0 shadow-md rounded-2xl mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-800">
              <strong>Not traveling with us?</strong> No problem! Ship your barrels, suitcases, scooters, bicycles, TVs, 
              and more with T&M Courier Services. Fill out this form and we'll contact you to arrange pickup and delivery.
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl rounded-3xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white py-6">
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6" />
              Shipping Request Form
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="space-y-4">
                <h3 className="font-bold text-lg border-b pb-2">Your Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block">Full Name *</Label>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      data-testid="shipping-name-input"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Phone Number *</Label>
                    <PhoneInput
                      value={formData.phone_number}
                      onChange={(val) => setFormData(prev => ({ ...prev, phone_number: val }))}
                      placeholder="Phone number"
                      dataTestId="shipping-phone-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="mb-2 block">Email</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    data-testid="shipping-email-input"
                  />
                </div>
              </div>

              {/* Addresses */}
              <div className="space-y-4">
                <h3 className="font-bold text-lg border-b pb-2">Pickup & Delivery</h3>
                
                <div>
                  <Label className="mb-2 block">Pickup Address *</Label>
                  <AddressAutocomplete
                    value={formData.pickup_address}
                    onChange={(val) => setFormData(prev => ({ ...prev, pickup_address: val }))}
                    placeholder="Start typing pickup address..."
                    dataTestId="shipping-pickup-input"
                  />
                </div>
                
                <div>
                  <Label className="mb-2 block">Delivery Address *</Label>
                  <AddressAutocomplete
                    value={formData.dropoff_address}
                    onChange={(val) => setFormData(prev => ({ ...prev, dropoff_address: val }))}
                    placeholder="Start typing delivery address..."
                    dataTestId="shipping-dropoff-input"
                  />
                </div>
                
                <div>
                  <Label className="mb-2 block">Preferred Pickup Date</Label>
                  <Input
                    name="preferred_date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={handleInputChange}
                    data-testid="shipping-date-input"
                  />
                </div>
              </div>

              {/* Items */}
              <div className="space-y-4">
                <h3 className="font-bold text-lg border-b pb-2">Items to Ship</h3>
                <p className="text-sm text-muted-foreground">Select the items you need to ship</p>
                
                <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-sm">
                  <p className="font-semibold text-green-800 mb-1">Pricing:</p>
                  <p className="text-green-700">Barrels: <span className="font-bold">$200</span> | Boxes: <span className="font-bold">$350</span></p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'suitcases', label: 'Suitcases', icon: '🧳', price: null },
                    { key: 'barrels', label: 'Barrels', icon: '🛢️', price: '$200' },
                    { key: 'boxes', label: 'Boxes', icon: '📦', price: '$350' },
                    { key: 'bicycles', label: 'Bicycles', icon: '🚲', price: null },
                    { key: 'scooters', label: 'Scooters', icon: '🛴', price: null },
                    { key: 'tvs', label: 'TVs', icon: '📺', price: null },
                    { key: 'miscellaneous', label: 'Misc Items', icon: '📋', price: null }
                  ].map(item => (
                    <div 
                      key={item.key} 
                      className={`p-4 rounded-xl border-2 transition-colors ${
                        items[item.key] > 0 ? 'border-green-500 bg-green-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="text-center mb-2">
                        <span className="text-2xl">{item.icon}</span>
                        <p className="text-sm font-medium">{item.label}</p>
                        {item.price && <p className="text-xs font-bold text-green-600">{item.price}</p>}
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => updateItemCount(item.key, -1)} className="w-8 h-8 p-0 rounded-full">
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-bold">{items[item.key]}</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => updateItemCount(item.key, 1)} className="w-8 h-8 p-0 rounded-full">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {getTotalItems() > 0 && (
                  <div className="bg-green-50 p-4 rounded-xl text-center">
                    <p className="font-bold text-green-800">Total Items: {getTotalItems()}</p>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <Label className="mb-2 block">Additional Notes</Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any special instructions or details about your items..."
                  rows={3}
                  data-testid="shipping-notes-input"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-green-600 hover:bg-green-700 rounded-xl text-lg font-bold"
                data-testid="submit-shipping-btn"
              >
                {loading ? "Submitting..." : "Submit Shipping Request"}
              </Button>
            </form>

            {/* Contact Info */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-sm text-muted-foreground mb-2">Questions? Contact T&M Courier Services:</p>
              <p className="font-bold text-green-700">(815) 914-8139</p>
              <p className="text-sm text-green-600">Tmcourierservice1@gmail.com</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
